import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowPathIcon,
  ChevronUpDownIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { adminApi } from '../services/api';

interface Chapter {
  chapter_id: number;
  name: string;
  position: number;
}

interface Subject {
  subject_id: number;
  name: string;
}

// Sortable Chapter Item Component
function SortableChapterItem({ chapter, index, isDragging }: {
  chapter: Chapter;
  index: number;
  isDragging: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isItemDragging,
  } = useSortable({ id: chapter.chapter_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border ${
        isItemDragging ? 'opacity-50 border-indigo-500' : 'border-gray-200 dark:border-gray-700'
      } ${isDragging && !isItemDragging ? 'border-dashed' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            className="cursor-move p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            {...attributes}
            {...listeners}
          >
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
          </button>
          <span className="text-lg font-semibold text-gray-500 dark:text-gray-400">
            {chapter.position}
          </span>
          <span className="text-gray-700 dark:text-gray-300 font-medium">
            {chapter.name}
          </span>
        </div>
        <span className="text-xs text-gray-400">
          ID: {chapter.chapter_id}
        </span>
      </div>
    </div>
  );
}

export default function ChapterManager() {
  const [selectedSubject, setSelectedSubject] = useState<number | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch subjects
  const { data: subjectsData, isLoading: loadingSubjects } = useQuery({
    queryKey: ['subjects-list'],
    queryFn: () => adminApi.getSubjectsAndChapters(),
  });

  // Fetch chapters for selected subject
  const { data: chaptersData, isLoading: loadingChapters, refetch: refetchChapters } = useQuery({
    queryKey: ['chapters-positions', selectedSubject],
    queryFn: () => adminApi.getChaptersWithPositions(selectedSubject!),
    enabled: !!selectedSubject,
  });

  // Update chapters when data changes
  useEffect(() => {
    if (chaptersData?.data?.chapters) {
      setChapters(chaptersData.data.chapters);
      setHasChanges(false);
    }
  }, [chaptersData]);

  // Mutations
  const reorderMutation = useMutation({
    mutationFn: ({ subjectId, chapterIds }: { subjectId: number; chapterIds: number[] }) =>
      adminApi.reorderChapters(subjectId, chapterIds),
    onSuccess: () => {
      toast.success('Chapters reordered successfully!');
      setHasChanges(false);
      refetchChapters();
    },
    onError: () => {
      toast.error('Failed to reorder chapters');
    },
  });

  const normalizeMutation = useMutation({
    mutationFn: () => adminApi.normalizeAllPositions(),
    onSuccess: () => {
      toast.success('All positions normalized successfully!');
      refetchChapters();
    },
    onError: () => {
      toast.error('Failed to normalize positions');
    },
  });

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = event;

    if (active.id !== over?.id) {
      setChapters((items) => {
        const oldIndex = items.findIndex((item) => item.chapter_id === active.id);
        const newIndex = items.findIndex((item) => item.chapter_id === over?.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update positions
        return newItems.map((item, index) => ({
          ...item,
          position: index + 1,
        }));
      });
      setHasChanges(true);
    }
  };

  const handleSaveOrder = () => {
    if (!selectedSubject || !hasChanges) return;

    const chapterIds = chapters.map(c => c.chapter_id);
    reorderMutation.mutate({ subjectId: selectedSubject, chapterIds });
  };

  const handleNormalizeAll = () => {
    if (confirm('This will normalize all positions across the entire database. Continue?')) {
      normalizeMutation.mutate();
    }
  };

  const subjects = subjectsData?.data?.subjects || [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Chapter Position Manager
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Drag and drop to reorder chapters within subjects
        </p>
      </div>

      {/* Subject Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Subject
        </label>
        <select
          value={selectedSubject || ''}
          onChange={(e) => setSelectedSubject(e.target.value ? Number(e.target.value) : null)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                   focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">Select a subject...</option>
          {subjects.map((subject: Subject) => (
            <option key={subject.subject_id} value={subject.subject_id}>
              {subject.name}
            </option>
          ))}
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        {selectedSubject && (
          <>
            <button
              onClick={handleSaveOrder}
              disabled={!hasChanges || reorderMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg
                       hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
            >
              {reorderMutation.isPending ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Save Order
                </>
              )}
            </button>

            {hasChanges && (
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <span>Unsaved changes</span>
              </div>
            )}
          </>
        )}

        <button
          onClick={handleNormalizeAll}
          disabled={normalizeMutation.isPending}
          className="ml-auto flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg
                   hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed
                   transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Normalize All Positions
        </button>
      </div>

      {/* Chapters List */}
      {selectedSubject && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
          {loadingChapters ? (
            <div className="text-center py-8">
              <svg className="animate-spin h-8 w-8 mx-auto text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading chapters...</p>
            </div>
          ) : chapters.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">No chapters found for this subject</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={chapters.map(c => c.chapter_id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {chapters.map((chapter, index) => (
                    <SortableChapterItem
                      key={chapter.chapter_id}
                      chapter={chapter}
                      index={index}
                      isDragging={isDragging}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
          About Chapter Ordering
        </h3>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Chapters are displayed in their current position order</li>
          <li>• Drag and drop chapters to reorder them</li>
          <li>• Changes are not saved until you click "Save Order"</li>
          <li>• Use "Normalize All Positions" to clean up position numbers across the entire database</li>
        </ul>
      </div>
    </div>
  );
}